import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PortalApplicationsService } from '../../services/portal/portalApplicationsService';
import ApplicationProgressTracker from '../../components/client/applications/ApplicationProgressTracker';
import OfferCard from '../../components/client/applications/OfferCard';
import { ArrowLeftIcon, DocumentIcon, CheckCircleIcon, ClockIcon } from '@heroicons/react/24/outline';

const DocumentChecklist = ({ documents }) => {
    if (!documents?.length) return null;

    const completed = documents.filter(d => d.status === 'completed' || d.status === 'approved').length;
    const progress = Math.round((completed / documents.length) * 100);

    return (
        <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h3 className="text-lg font-semibold text-slate-900">Document Checklist</h3>
                    <p className="text-sm text-slate-500 mt-0.5">
                        {completed} of {documents.length} completed
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <div className="w-20 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all duration-500"
                            style={{ width: `${progress}%` }}
                        ></div>
                    </div>
                    <span className="text-sm font-medium text-indigo-600">{progress}%</span>
                </div>
            </div>

            <ul className="space-y-2">
                {documents.map(doc => (
                    <li
                        key={doc.id}
                        className={`
                            flex items-center gap-3 p-3 rounded-xl transition-colors
                            ${doc.status === 'completed' || doc.status === 'approved'
                                ? 'bg-emerald-50'
                                : doc.status === 'pending'
                                    ? 'bg-amber-50'
                                    : 'bg-slate-50'}
                        `}
                    >
                        <div className={`
                            h-8 w-8 rounded-lg flex items-center justify-center
                            ${doc.status === 'completed' || doc.status === 'approved'
                                ? 'bg-emerald-100 text-emerald-600'
                                : doc.status === 'pending'
                                    ? 'bg-amber-100 text-amber-600'
                                    : 'bg-slate-100 text-slate-500'}
                        `}>
                            {doc.status === 'completed' || doc.status === 'approved' ? (
                                <CheckCircleIcon className="h-5 w-5" />
                            ) : doc.status === 'pending' ? (
                                <ClockIcon className="h-5 w-5" />
                            ) : (
                                <DocumentIcon className="h-5 w-5" />
                            )}
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-900 truncate">{doc.title}</p>
                        </div>
                        <span className={`
                            text-xs font-medium px-2 py-1 rounded-full capitalize
                            ${doc.status === 'completed' || doc.status === 'approved'
                                ? 'bg-emerald-100 text-emerald-700'
                                : doc.status === 'pending'
                                    ? 'bg-amber-100 text-amber-700'
                                    : 'bg-slate-100 text-slate-600'}
                        `}>
                            {doc.status}
                        </span>
                    </li>
                ))}
            </ul>
        </section>
    );
};

const StatusTimeline = ({ history }) => {
    if (!history?.length) return null;

    return (
        <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Status History</h3>
            <div className="space-y-4">
                {history.map((event, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                        <div className="w-2 h-2 mt-2 rounded-full bg-indigo-500 flex-shrink-0"></div>
                        <div>
                            <p className="text-sm font-medium text-slate-900">{event.title || event.status}</p>
                            <p className="text-xs text-slate-500">
                                {event.date ? new Date(event.date).toLocaleDateString() : ''}
                            </p>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
};

const ClientApplicationDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [application, setApplication] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (id) loadApplication();
    }, [id]);

    const loadApplication = async () => {
        setLoading(true);
        const { data } = await PortalApplicationsService.getUniversityApplication(id);
        setApplication(data);
        setLoading(false);
    };

    const getCurrentStep = () => {
        if (!application?.status) return 'draft';
        const status = application.status.toLowerCase();
        if (status.includes('draft')) return 'draft';
        if (status.includes('submit')) return 'documents';
        if (status.includes('process') || status.includes('review')) return 'review';
        if (status.includes('decision') || status.includes('wait')) return 'decision';
        if (status.includes('offer') || status.includes('accept')) return 'offer';
        return 'review';
    };

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="h-10 w-40 bg-slate-100 rounded-xl animate-pulse"></div>
                <div className="h-48 bg-slate-100 rounded-2xl animate-pulse"></div>
                <div className="h-32 bg-slate-100 rounded-2xl animate-pulse"></div>
            </div>
        );
    }

    if (!application) {
        return (
            <div className="text-center py-12">
                <div className="text-5xl mb-4">😕</div>
                <h2 className="text-xl font-semibold text-slate-900 mb-2">Application not found</h2>
                <p className="text-slate-500 mb-6">We couldn't find this application.</p>
                <button
                    onClick={() => navigate('/portal/applications')}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors"
                >
                    Back to Applications
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Back button */}
            <button
                onClick={() => navigate('/portal/applications')}
                className="flex items-center gap-2 text-slate-500 hover:text-slate-900 transition-colors group"
            >
                <ArrowLeftIcon className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
                Back to Applications
            </button>

            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl p-6 text-white shadow-lg">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold">
                            {application.programs?.university?.name || 'University Application'}
                        </h1>
                        <p className="text-indigo-100 mt-1 text-base">
                            {application.programs?.name || 'Program'}
                        </p>
                        {application.intake?.term && (
                            <p className="text-indigo-200 text-sm mt-1">{application.intake.term}</p>
                        )}
                    </div>
                    <span className="self-start sm:self-center px-4 py-2 bg-white/20 backdrop-blur rounded-xl text-sm font-medium capitalize">
                        {application.status || 'Draft'}
                    </span>
                </div>
            </div>

            {/* Progress Tracker */}
            <ApplicationProgressTracker
                currentStep={getCurrentStep()}
                application={{
                    university: application.programs?.university?.name
                }}
            />

            {/* Main Content */}
            <div className="grid gap-6 lg:grid-cols-3">
                {/* Left Column - Documents & Offers */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Offers */}
                    {application.offers && application.offers.length > 0 && (
                        <section>
                            <h3 className="text-lg font-semibold text-slate-900 mb-4">🎉 Offers Received</h3>
                            <div className="grid gap-4 sm:grid-cols-2">
                                {application.offers.map(offer => (
                                    <OfferCard key={offer.id} offer={offer} onUpdate={loadApplication} />
                                ))}
                            </div>
                        </section>
                    )}

                    {/* Document Checklist */}
                    <DocumentChecklist documents={application.documents} />
                </div>

                {/* Right Column - Status Timeline */}
                <div>
                    <StatusTimeline history={application.status_history} />

                    {/* Quick Actions */}
                    <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
                        <h3 className="text-base font-semibold text-slate-900 mb-3">Need Help?</h3>
                        <button
                            onClick={() => navigate('/portal/messages')}
                            className="w-full px-4 py-3 bg-indigo-50 text-indigo-600 rounded-xl text-sm font-medium hover:bg-indigo-100 transition-colors flex items-center justify-center gap-2"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                            </svg>
                            Contact Your Consultant
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ClientApplicationDetailPage;
